import {
  IconButton,
  List,
  ListItemButton,
  ListItemProps,
  ListItemSecondaryAction,
  ListItemText,
  ListProps,
  Tooltip
} from '@mui/material'
import { Aurora } from '../types'
import { useTranslation } from 'react-i18next'
import { humanizeTimestamp } from '../utils/date'
import { OpenInNew } from '@mui/icons-material'
import { NavLink } from 'react-router-dom'

export interface AuroraDetailsListProps extends Omit<ListProps, 'children'> {
  aurora: Aurora
  withSecondaryActions?: boolean
  withCatalogProduct?: boolean
  withDevice?: boolean
  withCompany?: boolean
  withTimestamps?: boolean
  onAuroraChange?: (aurora: Aurora) => void
}

export default function AuroraDetailsList({
  aurora,
  withSecondaryActions = true,
  withCatalogProduct = true,
  withDevice = false,
  withCompany = true,
  withTimestamps = true,
  onAuroraChange,
  ...props
}: AuroraDetailsListProps) {
  const { t } = useTranslation('aurora')

  return (
    <List {...props}>
      <AuroraIdListItem
        key="id"
        aurora={aurora}
        withSecondaryAction={withSecondaryActions}
        onAuroraChange={onAuroraChange}
      />

      <AuroraCatalogProductListItem
        key="catalogProduct"
        aurora={aurora}
        withSecondaryAction={withSecondaryActions}
        onAuroraChange={onAuroraChange}
      />

      <AuroraHeatingPowerListItem
        key="heatingPower"
        aurora={aurora}
        withSecondaryAction={withSecondaryActions}
        onAuroraChange={onAuroraChange}
      />

      <AuroraSimIdListItem
        key="simId"
        aurora={aurora}
        withSecondaryAction={withSecondaryActions}
        onAuroraChange={onAuroraChange}
      />

      <AuroraUnoIdListItem
        key="unoId"
        aurora={aurora}
        withSecondaryAction={withSecondaryActions}
        onAuroraChange={onAuroraChange}
      />

      {withCompany && (
        <AuroraCompanyListItem
          key="company"
          aurora={aurora}
          withSecondaryAction={withSecondaryActions}
          onAuroraChange={onAuroraChange}
        />
      )}

      <AuroraCustomerReferenceListItem
        key="customerReference"
        aurora={aurora}
        withSecondaryAction={withSecondaryActions}
        onAuroraChange={onAuroraChange}
      />

      {withDevice && (
        <AuroraDeviceListItem
          key="device"
          aurora={aurora}
          withSecondaryAction={withSecondaryActions}
          onAuroraChange={onAuroraChange}
        />
      )}

      {withTimestamps && (
        <ListItemButton key="createdAt">
          <ListItemText
            primary={aurora.createdAt ? humanizeTimestamp(aurora.createdAt) : t('noCreatedAt')}
            secondary={t('createdAt')}
            primaryTypographyProps={{ color: aurora.createdAt ? 'text.primary' : 'text.disabled' }}
          />
        </ListItemButton>
      )}

      {withTimestamps && (
        <ListItemButton key="updatedAt">
          <ListItemText primary={humanizeTimestamp(aurora.updatedAt)} secondary={t('updatedAt')} />
        </ListItemButton>
      )}
    </List>
  )
}

interface AuroraDetailsListItemProps extends Omit<ListItemProps, 'children'> {
  aurora: Aurora
  withSecondaryAction?: boolean
  onAuroraChange?: (aurora: Aurora) => void
}

function AuroraIdListItem({ aurora }: AuroraDetailsListItemProps) {
  const { t } = useTranslation('aurora')

  return (
    <ListItemButton>
      <ListItemText secondary={t('id')} primary={aurora.id} />
    </ListItemButton>
  )
}

function AuroraSimIdListItem({ aurora }: AuroraDetailsListItemProps) {
  const { t } = useTranslation('aurora')

  if (aurora.simId === null) {
    return null
  }

  return (
    <ListItemButton>
      <ListItemText secondary={t('sim')} primary={aurora.simId || t('noSim')} />
    </ListItemButton>
  )
}

function AuroraUnoIdListItem({ aurora }: AuroraDetailsListItemProps) {
  const { t } = useTranslation('aurora')

  if (aurora.unoId === null) {
    return null
  }

  return (
    <ListItemButton>
      <ListItemText secondary={t('uno')} primary={aurora.unoId || t('noUno')} />
    </ListItemButton>
  )
}

function AuroraCatalogProductListItem({ aurora }: AuroraDetailsListItemProps) {
  const { t } = useTranslation('aurora')

  if (aurora?.catalogProduct === null) {
    return null
  }

  return (
    <>
      <ListItemButton key="catalogProduct.name">
        <ListItemText
          secondary={t('catalogProduct')}
          primary={aurora?.catalogProduct?.name || t('noCatalogProduct')}
          primaryTypographyProps={{
            color: aurora?.catalogProduct?.name ? 'text.primary' : 'text.disabled'
          }}
        />
      </ListItemButton>

      <ListItemButton key="catalogProduct.manufacturer">
        <ListItemText
          secondary={t('catalogProduct:manufacturer')}
          primary={aurora?.catalogProduct?.manufacturer || t('catalogProduct:noManufacturer')}
          primaryTypographyProps={{
            color: aurora?.catalogProduct?.manufacturer ? 'text.primary' : 'text.disabled'
          }}
        />
      </ListItemButton>
    </>
  )
}

function AuroraHeatingPowerListItem({ aurora }: AuroraDetailsListItemProps) {
  const { t } = useTranslation('aurora')

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('heatingPower')}
        primary={aurora.heatingPower !== null ? `${aurora.heatingPower} kW` : t('noHeatingPower')}
        primaryTypographyProps={{
          color: aurora.heatingPower !== null ? 'text.primary' : 'text.disabled'
        }}
      />
    </ListItemButton>
  )
}

function AuroraCustomerReferenceListItem({ aurora }: AuroraDetailsListItemProps) {
  const { t } = useTranslation('aurora')

  const hasCustomerReference = aurora.customerReference !== null && aurora.customerReference !== ''

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('customerReference')}
        primary={hasCustomerReference ? aurora.customerReference : t('noCustomerReference')}
        primaryTypographyProps={{
          color: hasCustomerReference ? 'text.primary' : 'text.disabled'
        }}
      />
    </ListItemButton>
  )
}

function AuroraCompanyListItem({ aurora }: AuroraDetailsListItemProps) {
  const { t } = useTranslation('aurora')

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('company')}
        primary={aurora?.company?.name || t('noCompany')}
        primaryTypographyProps={{
          color: aurora?.company?.name ? 'text.primary' : 'text.disabled'
        }}
      />
    </ListItemButton>
  )
}

function AuroraDeviceListItem({ aurora, withSecondaryAction }: AuroraDetailsListItemProps) {
  const { t } = useTranslation('aurora')

  return (
    <ListItemButton>
      <ListItemText
        primary={aurora.device ? <>{aurora.device.shortUUID}</> : <>{t('noDevice')}</>}
        primaryTypographyProps={{ color: aurora?.device ? 'text.primary' : 'text.disabled' }}
        secondary={t('device')}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {aurora?.device && (
            <Tooltip title={t('actions:show')}>
              <IconButton component={NavLink} to={`/devices/${aurora?.device.name}`}>
                <OpenInNew />
              </IconButton>
            </Tooltip>
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}
